import {ForwardedRef, useCallback, useId, useMemo, useRef, useState} from 'react';

import {useComposedRef} from 'shared/web';

import {RichTextProps} from '../types';
import {plainTextLength} from '../utils/plainTextLength';

let instanceCounter = 0;

export function useRichTextEditor(props: RichTextProps, userRef: ForwardedRef<HTMLDivElement>) {
  const id = useId();
  const helperId = `${id}-helper`;
  // React's useId() generates IDs with colons (e.g. ":r1iq:") which are invalid
  // in CSS selectors. Quill uses querySelector with this ID, so we need a plain ID.
  const toolbarId = useMemo(() => `richtext-toolbar-${++instanceCounter}`, []);
  const containerRef = useRef<HTMLDivElement>(null);
  const ref = useComposedRef(containerRef, userRef);

  const [isFocused, setIsFocused] = useState(false);

  const handleFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
  }, []);

  // Memoized so renders that don't change the value (selection, image drag, emoji toggle, …) skip
  // re-parsing the HTML — important when the value carries a large base64 image.
  const currentLength = useMemo(() => plainTextLength(props.value), [props.value]);
  const isOverLimit = !!props.maxLength && currentLength > props.maxLength;
  const warningThreshold = props.warningThreshold ?? 0.8;
  const isNearLimit =
    !!props.maxLength && !isOverLimit && currentLength >= props.maxLength * warningThreshold;

  return {
    id,
    helperId,
    toolbarId,
    ref,
    isFocused,
    currentLength,
    isOverLimit,
    isNearLimit,
    handleFocus,
    handleBlur,
    onChange: props.onChange,
  };
}
